import { RuleId } from '@wix/a11y-rules';
import { ITEM_TYPES } from '@wix/advanced-seo-utils';
import { Text } from '@wix/design-system';
import { Trans, useTranslation } from '@wix/fe-essentials/react';
import { AltTextScreenContent, AltTextScreenFooter, AltTextScreenLayout, AltTextScreenProvider, usePaginatedImages } from '@wix/rules-panel-ui';
import { assertNotOptional } from '@wix/seo-common-core';
import { useAtomValue, useSetAtom } from 'jotai';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { altTextScreenPageIdAtom, verticalsDrillInPageIdAtom } from '../../../../../../atoms/session-atoms';
import { BiCtaNames } from '../../../../../../bi/types';
import { usePanelStateAtom } from '../../../../../../hooks/rules-state/use-panel-state-atom';
import { useA11yWizardAdapter } from '../../../../../../hooks/use-a11y-wizard-adapter';
import { useBiLogger } from '../../../../../../hooks/use-bi-logger';
import { usePanelCloseHandler } from '../../../../../../hooks/use-panel-close-handler';
import { usePanoramaFunctions } from '../../../../../../panorama/use-panorama-functions';
import { getUpdatedAltTextImagesFromState } from '../../../../../../rule-mappers/rule-handlers/alt-text/get-updated-alt-text-images-from-state';
import { useBaseAltTextRuleHandlers } from '../../../../../../rule-mappers/rule-handlers/alt-text-v2/use-base-alt-text-rule-handlers';
import { useStaticPagesAltTextHandlers } from '../../../../../../rule-mappers/rule-handlers/alt-text-v2/use-static-pages-alt-text-handlers';
import { useGetAltTextHandlersFromItemType } from '../../hooks/use-get-alt-text-handlers-from-item-type';
import { useAiGeneration, useApplyChanges, useBiCallbacks, useImageGroups, useImageState } from './hooks';
import { buildTranslations } from './translations';
import { PAGE_SIZE } from './types';
export const SharedAltTextScreenWrapper = _ref => {
  var _pagesData$get, _ruleState$state2;
  let {
    pageId,
    itemType
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    getGroupData,
    panelState,
    getPagesData,
    getRuleState
  } = usePanelStateAtom();
  const pagesData = getPagesData();
  const {
    a11yWizardAdapter,
    capabilities,
    editorHost
  } = useA11yWizardAdapter();
  const biLogger = useBiLogger();
  const {
    reportErrorPanorama
  } = usePanoramaFunctions();
  const compactPanelChrome = capabilities.compactPanelChrome;
  const altTextScreenState = useAtomValue(altTextScreenPageIdAtom);
  const setAltTextScreenPageId = useSetAtom(altTextScreenPageIdAtom);
  const setVerticalsDrillInPageId = useSetAtom(verticalsDrillInPageIdAtom);
  const altHandlers = useGetAltTextHandlersFromItemType({
    itemType
  });
  const staticAltHandlers = useStaticPagesAltTextHandlers();
  const baseAltHandlers = useBaseAltTextRuleHandlers();
  const getAltTextScreenData = useCallback(() => {
    if (itemType === ITEM_TYPES.STATIC_PAGE_V2) {
      var _getGroupData;
      return (_getGroupData = getGroupData(pageId)) == null || (_getGroupData = _getGroupData.ruleExecutionResults) == null ? void 0 : _getGroupData.filter(result => result.ruleId === RuleId.STATIC_ALT_TEXT);
    }
    return Array.from(panelState.groupsDataMap.entries()).filter(_ref2 => {
      let [_, groupData] = _ref2;
      return groupData.itemType === itemType;
    }).flatMap(_ref3 => {
      var _groupData$ruleExecut;
      let [_, groupData] = _ref3;
      return ((_groupData$ruleExecut = groupData.ruleExecutionResults) == null ? void 0 : _groupData$ruleExecut.filter(result => result.ruleId === RuleId.STATIC_ALT_TEXT)) ?? [];
    });
  }, [getGroupData, itemType, pageId, panelState.groupsDataMap]);
  const pageResult = useMemo(() => getAltTextScreenData(), [getAltTextScreenData]);
  assertNotOptional(pageResult, 'pageResult');
  assertNotOptional(altHandlers, 'altHandlers');
  const altImageProps = useMemo(() => baseAltHandlers.extractAltImageProps(pageResult, pageId), [baseAltHandlers, pageId, pageResult]);
  const initialAltRulesProps = useMemo(() => altHandlers != null && altHandlers.getElementOrder ? altHandlers.getElementOrder(altImageProps) : altImageProps, [altHandlers, altImageProps]);
  const pageName = ((_pagesData$get = pagesData.get(pageId)) == null ? void 0 : _pagesData$get.title) ?? '';
  const ruleState = getRuleState(pageId, RuleId.STATIC_ALT_TEXT);
  const altRulesPropsWithState = useMemo(() => {
    var _ruleState$state;
    return getUpdatedAltTextImagesFromState(initialAltRulesProps, ruleState == null || (_ruleState$state = ruleState.state) == null ? void 0 : _ruleState$state.savedImages, panelState.globalSectionComponentsDataMap);
  }, [initialAltRulesProps, panelState.globalSectionComponentsDataMap, ruleState == null || (_ruleState$state2 = ruleState.state) == null ? void 0 : _ruleState$state2.savedImages]);

  // UI state - declared before useImageState so we can pass setters for reset behavior
  const [showSummary, setShowSummary] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState(null);
  const imageState = useImageState({
    altRulesPropsWithState,
    setSelectedImageId,
    setShowSummary
  });
  const {
    images,
    setImages,
    originalPropsMap,
    modifiedImageIds,
    setModifiedImageIds,
    hasChanges,
    setHasChanges,
    hasAiGeneratedContent,
    setHasAiGeneratedContent,
    filledCount,
    missingCount,
    hasModifiedImages,
    resetToOriginal,
    commitCurrentAsOriginal,
    handleUpdateAltText,
    handleUpdateDecorative
  } = imageState;
  const navigateToPanel = useCallback(() => {
    setAltTextScreenPageId(undefined);
    if ((altTextScreenState == null ? void 0 : altTextScreenState.returnTo) === 'main') {
      setVerticalsDrillInPageId(undefined);
    }
  }, [altTextScreenState == null ? void 0 : altTextScreenState.returnTo, setAltTextScreenPageId, setVerticalsDrillInPageId]);
  const closePanel = useCallback(() => {
    setAltTextScreenPageId(undefined);
    setVerticalsDrillInPageId(undefined);
    a11yWizardAdapter.togglePanel();
  }, [a11yWizardAdapter, setAltTextScreenPageId, setVerticalsDrillInPageId]);

  // Pagination via shared hook from rules-panel-ui
  const contentRef = useRef(null);
  const handlePaginationClick = useCallback(params => {
    const ctaName = params.direction === 'next' ? params.isTopPagination ? BiCtaNames.TopPaginationNext : BiCtaNames.BottomPaginationNext : params.isTopPagination ? BiCtaNames.TopPaginationPrev : BiCtaNames.BottomPaginationPrev;
    biLogger.taskCtaClick({
      ctaName,
      elementCount: images.length,
      failedElementCount: missingCount
    });
  }, [biLogger, images.length, missingCount]);
  const {
    paginatedImages,
    paginationConfig,
    currentPage,
    totalPages
  } = usePaginatedImages(images, {
    compactPanelLayout: compactPanelChrome,
    contentRef,
    onPaginationClick: handlePaginationClick,
    pageSize: PAGE_SIZE
  });

  // Image groups (a11y-wizard-specific: groups by entityId for products, blog posts, etc.)
  const imageGroups = useImageGroups({
    altHandlers,
    images: paginatedImages,
    itemType,
    pageId
  });
  const biCallbacks = useBiCallbacks({
    biLogger,
    images,
    missingCount
  });
  const aiGeneration = useAiGeneration({
    a11yWizardAdapter,
    biLogger,
    currentPage,
    editorHost,
    images,
    itemType,
    onGenerateComplete: biCallbacks.onWarningShown,
    pageId,
    pageSize: PAGE_SIZE,
    reportErrorPanorama,
    setHasAiGeneratedContent,
    setHasChanges,
    setImages,
    setModifiedImageIds,
    setShowSummary,
    t
  });
  const {
    isGenerating,
    setIsGenerating,
    generatingImageIds,
    unreviewedAiImageIds,
    handleGenerateAi,
    handleGenerateSingleImage,
    handleRetryFailed,
    handleSortUnsupported
  } = aiGeneration;
  const applyChanges = useApplyChanges({
    a11yWizardAdapter,
    altHandlers,
    altRulesProps: altRulesPropsWithState,
    biLogger,
    commitCurrentAsOriginal,
    editorHost,
    hasChanges,
    images,
    isGenerating,
    missingCount,
    modifiedImageIds,
    navigateToPanel,
    originalPropsMap,
    pageId,
    reportErrorPanorama,
    resetToOriginal,
    setHasChanges,
    setIsGenerating,
    setModifiedImageIds,
    setShowSummary,
    staticAltHandlers,
    t,
    unreviewedAiImageIds
  });
  const {
    isSaving,
    setIsSaving,
    handleApply,
    handleCancel,
    showBeforeCloseModal,
    unsavedChangesHandlers
  } = applyChanges;
  const handleCloseAttempt = useCallback(() => {
    biCallbacks.onCloseClick();
  }, [biCallbacks]);
  const handleClosePanel = useCallback(() => {
    const hasUnsavedChanges = hasChanges || modifiedImageIds.size > 0;
    if (hasUnsavedChanges || isGenerating) {
      showBeforeCloseModal(closePanel, isGenerating);
    } else {
      closePanel();
    }
  }, [closePanel, hasChanges, isGenerating, modifiedImageIds.size, showBeforeCloseModal]);
  const handleCloseWithModal = useCallback(closePanelDirectly => {
    if (hasChanges || modifiedImageIds.size > 0 || isGenerating) {
      showBeforeCloseModal(closePanelDirectly, isGenerating);
      return true;
    }
    return false;
  }, [hasChanges, isGenerating, modifiedImageIds.size, showBeforeCloseModal]);
  usePanelCloseHandler({
    handleCloseWithModal,
    onCloseAttemptBI: handleCloseAttempt
  });

  // Open image in editor
  const handleOpenImage = useCallback(image => {
    if (altHandlers.openImageElement) {
      const originalProps = originalPropsMap.get(image.id);
      if (originalProps) {
        altHandlers.openImageElement(originalProps);
      }
    }
  }, [altHandlers, originalPropsMap]);

  // Translations
  const formatPaginationRange = useCallback((start, end, total) => /*#__PURE__*/React.createElement(Text, {
    size: "tiny"
  }, /*#__PURE__*/React.createElement(Trans, {
    components: {
      grey: /*#__PURE__*/React.createElement(Text, {
        size: "tiny",
        light: true,
        secondary: true
      })
    },
    i18nKey: "accessibility-wizard.alt-text.elements-list.element-indexes",
    values: {
      endIndex: end,
      startIndex: start,
      totalAmount: total
    }
  })), []);
  const translations = useMemo(() => ({
    ...buildTranslations({
      filledCount,
      pageName,
      t,
      totalCount: images.length
    }),
    formatPaginationRange
  }), [t, filledCount, images.length, pageName, formatPaginationRange]);

  // Build pagination config with formatItemsRange from translations
  const paginationConfigWithRange = useMemo(() => {
    if (!paginationConfig) {
      return undefined;
    }
    return {
      ...paginationConfig,
      formatItemsRange: translations.formatPaginationRange
    };
  }, [paginationConfig, translations.formatPaginationRange]);
  const contextValue = useMemo(() => ({
    actions: {
      onApply: handleApply,
      onBack: handleCancel,
      onCancel: handleCancel,
      onClose: handleCancel,
      onGenerateAi: handleGenerateAi,
      onGenerateSingleImage: handleGenerateSingleImage,
      onOpenImage: handleOpenImage,
      onRetryFailed: handleRetryFailed,
      onSortUnsupported: handleSortUnsupported,
      onToggleImage: id => setSelectedImageId(prev => prev === id ? null : id),
      onUpdateAltText: handleUpdateAltText,
      onUpdateDecorative: handleUpdateDecorative,
      setIsLoading: setIsSaving
    },
    biCallbacks,
    compactPanelChrome,
    design: {
      labelSize: compactPanelChrome ? 'small' : 'tiny'
    },
    editorHost,
    state: {
      allImages: images,
      currentPage,
      filledCount,
      generatingImageIds,
      groups: imageGroups,
      hasChanges,
      hasModifiedImages,
      images: paginatedImages,
      isApplyDisabled: isSaving || isGenerating || !hasModifiedImages,
      isGeneratingAi: isGenerating,
      isLoading: isSaving,
      missingCount,
      pageSize: PAGE_SIZE,
      previousSelectedImageId: null,
      scrollerRef: contentRef,
      selectedImageId,
      showAiWarning: hasAiGeneratedContent && !isGenerating,
      showDecorativeOption: altHandlers.enabledDecorativeImages,
      showOverviewDivider: !compactPanelChrome,
      showSummary,
      totalImages: images.length,
      totalPages
    },
    translations,
    unsavedChangesHandlers,
    wizardPresentation: capabilities.wizardPresentation
  }), [altHandlers.enabledDecorativeImages, biCallbacks, capabilities.wizardPresentation, currentPage, editorHost, compactPanelChrome, filledCount, generatingImageIds, handleApply, handleCancel, handleGenerateAi, handleGenerateSingleImage, handleOpenImage, handleRetryFailed, handleSortUnsupported, handleUpdateAltText, handleUpdateDecorative, hasAiGeneratedContent, hasChanges, hasModifiedImages, imageGroups, images, isGenerating, isSaving, missingCount, paginatedImages, selectedImageId, setIsSaving, showSummary, totalPages, translations, unsavedChangesHandlers]);
  return /*#__PURE__*/React.createElement(AltTextScreenProvider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(AltTextScreenLayout, {
    closeButtonAriaLabel: translations.closeButtonAriaLabel,
    content: /*#__PURE__*/React.createElement(AltTextScreenContent, {
      paginationConfig: paginationConfigWithRange
    }),
    footer: /*#__PURE__*/React.createElement(AltTextScreenFooter, {
      designProps: {
        buttonSize: compactPanelChrome ? 'small' : 'tiny'
      }
    }),
    headerTitle: translations.headerTitle,
    showHeader: !compactPanelChrome,
    onClickBack: () => {
      biCallbacks.onBackClick();
      handleCancel();
    },
    onClickClose: () => {
      biCallbacks.onCloseClick();
      handleClosePanel();
    }
  }));
};
//# sourceMappingURL=shared-alt-text-screen-wrapper.js.map